import { createFileRoute } from '@tanstack/react-router'
import CourseCard from '@/components/CourseCard'
import CourseProgressCard from '@/components/CourseProgressCard'
import ArticleCard from '@/components/ArticleCard'
import { getArticles } from '@/hooks/useArticle'
import { useEffect, useRef, useState } from 'react';
import { getAllCourses } from '@/hooks/useCourses';
import {
  FaBullseye,
  FaSearch,
  FaShieldAlt,
  FaUserShield,
  FaCompass,
  FaPiggyBank,
  FaHandsHelping,
} from 'react-icons/fa';

export const Route = createFileRoute('/_auth/learn')({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: typeof search.tab === 'string' ? search.tab : undefined,
    tour: typeof search.tour === 'string' ? search.tour : undefined,
    invitationcode: typeof search.invitationcode === 'string' ? search.invitationcode : undefined,
    __clerk_ticket: typeof search.__clerk_ticket === 'string' ? search.__clerk_ticket : undefined,
  }),
  component: RouteComponent,
})

function ArticleCardSkeleton() {
  return (
    <div className="rounded-2xl bg-white p-3 pb-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)]">
      <div className="h-48 w-full rounded-xl bg-slate-100" />
      <div className="px-2 py-5 space-y-3">
        <div className="flex gap-3">
          <div className="h-3 w-16 rounded bg-slate-100" />
          <div className="h-3 w-20 rounded bg-slate-100" />
        </div>
        <div className="h-5 w-3/4 rounded bg-slate-100" />
        <div className="space-y-2">
          <div className="h-4 w-full rounded bg-slate-100/70" />
          <div className="h-4 w-5/6 rounded bg-slate-100/70" />
        </div>
        <div className="flex gap-2 pt-1">
          <div className="h-6 w-16 rounded-full bg-slate-100" />
          <div className="h-6 w-20 rounded-full bg-slate-100" />
        </div>
      </div>
    </div>
  )
}

function CourseCardSkeleton() {
  return (
    <div className="rounded-2xl bg-white p-5 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)]">
      <div className="h-40 w-full rounded-xl bg-slate-100 mb-4" />
      <div className="h-5 w-2/3 rounded bg-slate-100 mb-3" />
      <div className="h-4 w-full rounded bg-slate-100/70 mb-2" />
      <div className="h-4 w-4/5 rounded bg-slate-100/70" />
    </div>
  )
}

function SkeletonGrid({ count, cols, children }: { count: number; cols: string; children: (i: number) => React.ReactNode }) {
  return (
    <div className={`grid ${cols} gap-6 animate-pulse`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i}>{children(i)}</div>
      ))}
    </div>
  )
}

function RouteComponent() {
  const { data: articles, isLoading, isError } = getArticles();
  const [search, setSearch] = useState('');
  const { data: courses, isLoading: isCoursesLoading, isError: isCoursesError } = getAllCourses();

  const tabs = ['Guides', 'Featured', 'By Skill', 'Videos', 'Courses', 'Downloads'];

  // --- Tab state from URL ---
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();
  const activeTab = tabs.includes((searchParams.tab as string)) ? (searchParams.tab as string) : 'Guides';
  // Update tab in URL
  const handleTabChange = (tab: string) => {
    navigate({
      search: prev => ({ ...prev, tab }),
      replace: false,
    });
  };

  // Horizontal scroll controls for tabs on small screens
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const element = tabsContainerRef.current;
    if (!element) return;
    const isMdOrLarger = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
    const isOverflowing = element.scrollWidth > element.clientWidth;
    setShowScrollButtons(!isMdOrLarger && isOverflowing);
    setCanScrollLeft(element.scrollLeft > 0);
    setCanScrollRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 1);
  };

  const scrollTabsBy = (delta: number) => {
    const element = tabsContainerRef.current;
    if (!element) return;
    element.scrollBy({ left: delta, behavior: 'smooth' });
    // Update state after the scroll animation starts
    requestAnimationFrame(() => {
      // small delay to let scroll position update
      setTimeout(updateScrollState, 150);
    });
  };

  const scrollTabsLeft = () => scrollTabsBy(-200);
  const scrollTabsRight = () => scrollTabsBy(200);

  useEffect(() => {
    updateScrollState();
    const handleResize = () => updateScrollState();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Define the 7 tier 1 behavior categories with their actual category IDs
  const skillCategories = [
    { 
      id: '1', 
      name: 'Scam Radar', 
      description: 'Learn to identify and avoid scams',
      categoryId: '88f96f6d-3c15-4aeb-bd44-8f39868717f6',
      icon: <FaBullseye className="w-7 h-7 text-violet-600" />,
    },
    { 
      id: '2', 
      name: 'Fact Finder', 
      description: 'Verify information and spot misinformation',
      categoryId: '8cd57c2e-3965-4f1f-ab64-1c18d08eeda3',
      icon: <FaSearch className="w-7 h-7 text-violet-600" />,
    },
    { 
      id: '3', 
      name: 'Threat Blocker', 
      description: 'Block and prevent security threats',
      categoryId: '66f900ad-dab3-4269-ae4d-4cc3527519e6',
      icon: <FaShieldAlt className="w-7 h-7 text-violet-600" />,
    },
    { 
      id: '4', 
      name: 'Privacy Protector', 
      description: 'Proactive security measures',
      categoryId: 'f4069ddc-145c-4c01-951f-708839f17113',
      icon: <FaUserShield className="w-7 h-7 text-violet-600" />,
    },
    { 
      id: '5', 
      name: 'Tech Navigator', 
      description: 'Navigate technology safely',
      categoryId: '867f4700-2ed4-49c5-82c9-99ba8c219b8b',
      icon: <FaCompass className="w-7 h-7 text-violet-600" />,
    },
    { 
      id: '6', 
      name: 'Finance Monitor', 
      description: 'Protect your financial information',
      categoryId: '5fafab18-74e1-48d7-a8ce-7e339d79fa78',
      icon: <FaPiggyBank className="w-7 h-7 text-violet-600" />,
    },
    { 
      id: '7', 
      name: 'Advisor', 
      description: 'Help others stay safe',
      categoryId: '',
      icon: <FaHandsHelping className="w-7 h-7 text-violet-600" />,
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl pt-10 px-6 sm:px-8 mx-auto pb-16 text-slate-900">

      {/* Course Progress Card */}
      <CourseProgressCard/>

      {/* Tabs Section */}
      <div className="mt-8" data-tour-id="tour-learn">
        <div className="relative">
          <div
            ref={tabsContainerRef}
            className="flex overflow-x-auto border-b border-slate-200 md:overflow-visible"
            onScroll={updateScrollState}
          >
            {tabs.map(tab => {
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  className={`relative -mb-px min-w-[110px] flex-1 cursor-pointer whitespace-nowrap px-4 pb-3 pt-1 text-center text-base font-semibold transition-colors duration-200 focus:outline-none ${
                    isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'
                  }`}
                  type="button"
                  tabIndex={0}
                  onClick={() => handleTabChange(tab)}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {tab}
                  <span
                    className={`pointer-events-none absolute inset-x-1/2 bottom-0 h-0.5 w-20 -translate-x-1/2 rounded-full transition-colors duration-200 ${
                      isActive ? 'bg-slate-900' : 'bg-transparent'
                    }`}
                  />
                </button>
              );
            })}
          </div>
          {showScrollButtons && (
            <>
              <button
                type="button"
                aria-label="Scroll tabs left"
                className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-slate-200 bg-white px-2 py-1 text-slate-600 shadow disabled:cursor-not-allowed disabled:opacity-40 md:hidden"
                onClick={scrollTabsLeft}
                disabled={!canScrollLeft}
              >
                ‹
              </button>
              <button
                type="button"
                aria-label="Scroll tabs right"
                className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-slate-200 bg-white px-2 py-1 text-slate-600 shadow disabled:cursor-not-allowed disabled:opacity-40 md:hidden"
                onClick={scrollTabsRight}
                disabled={!canScrollRight}
              >
                ›
              </button>
            </>
          )}
        </div>
      </div>
      {/* Courses Row */}
      {activeTab === 'Courses' && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">Courses For You</h2>
          </div>
          {isCoursesLoading ? (
            <SkeletonGrid count={3} cols="grid-cols-1 md:grid-cols-3">{() => <CourseCardSkeleton />}</SkeletonGrid>
          ) : isCoursesError ? (
            <div className="py-8 text-center text-red-500">Error loading courses.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {courses && [...courses].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).length > 0 ? (
                [...courses].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map(course => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    search={{ tab: activeTab }}
                  />
                ))
              ) : (
                <div className="py-8 text-center text-slate-500 col-span-3">No courses available.</div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Reading Section */}
      {activeTab === 'Featured' && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">Guides</h2>
            <button
              className="text-sm font-medium text-slate-500 transition-colors hover:text-slate-900 cursor-pointer"
              type="button"
              onClick={() => handleTabChange('Guides')}
            >
              View all
            </button>
          </div>
          {isLoading ? (
            <SkeletonGrid count={3} cols="grid-cols-1 md:grid-cols-2">{() => <ArticleCardSkeleton />}</SkeletonGrid>
          ) : isError ? (
            <div className="py-8 text-center text-red-500">Error loading articles.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {articles && [...articles].sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()).slice(0, 3).map(article => (
                <ArticleCard
                  key={article.id}
                  title={article.title}
                  description={article.description}
                  thumbnail={article.thumbnail}
                  link={{ to: `/articles/${article.slug}`, search: { tab: activeTab } }}
                  tags={article.tags || []}
                  articleType={article.articleType}
                  readTime={article.readTime}
                  updatedAt={article.updatedAt || article.publishedAt}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {/* Featured Printable Cheat Sheets Section */}
      {activeTab === 'Featured' && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">Printable Cheat Sheets</h2>
          </div>
          {isLoading ? (
            <SkeletonGrid count={4} cols="grid-cols-1 md:grid-cols-2">{() => <ArticleCardSkeleton />}</SkeletonGrid>
          ) : isError ? (
            <div className="py-8 text-center text-red-500">Error loading articles.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles && articles.filter(a => a.articleType === 'DOWNLOADABLE_PDF').sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()).slice(0, 3).map(article => (
                <ArticleCard
                  key={article.id}
                  title={article.title}
                  description={article.description}
                  thumbnail={article.thumbnail}
                  link={{ to: `/articles/${article.slug}`, search: { tab: activeTab } }}
                  tags={article.tags || []}
                  articleType={article.articleType}
                  readTime={article.readTime}
                  updatedAt={article.publishedAt || article.updatedAt}
                />
              ))}
              {articles && articles.filter(a => a.articleType === 'DOWNLOADABLE_PDF').length === 0 && (
                <div className="py-8 text-center text-slate-500 col-span-3">No printable cheat sheets available.</div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Featured Quick Lessons Section */}
      {activeTab === 'Featured' && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">Featured Courses</h2>
          </div>
          {isCoursesLoading ? (
            <SkeletonGrid count={3} cols="grid-cols-1 md:grid-cols-3">{() => <CourseCardSkeleton />}</SkeletonGrid>
          ) : isCoursesError ? (
            <div className="py-8 text-center text-red-500">Error loading courses.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {courses && [...courses].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 3).map(course => (
                <CourseCard
                  key={course.id}
                  course={course}
                  search={{ tab: activeTab }}
                />
              ))}
              {courses && courses.length === 0 && (
                <div className="py-8 text-center text-slate-500 col-span-3">No courses available.</div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Guides Tab Content */}
      {activeTab === 'Guides' && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">Guides</h2>
          </div>
          <div className="mb-4">
            <input
              type="text"
              className="w-full md:w-1/2 border border-slate-200 rounded-xl bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
              placeholder="Search guides by title or description..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {isLoading ? (
            <SkeletonGrid count={4} cols="grid-cols-1 md:grid-cols-2">{() => <ArticleCardSkeleton />}</SkeletonGrid>
          ) : isError ? (
            <div className="py-8 text-center text-red-500">Error loading articles.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {articles && articles.filter(a => a.articleType === 'ARTICLE')
                .filter(a => a.title.toLowerCase().includes(search.toLowerCase()) || a.description?.toLowerCase().includes(search.toLowerCase()))
                .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()).length > 0 ? (
                articles.filter(a => a.articleType === 'ARTICLE')
                  .filter(a => a.title.toLowerCase().includes(search.toLowerCase()) || a.description?.toLowerCase().includes(search.toLowerCase()))
                  .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
                  .map(article => (
                    <ArticleCard
                      key={article.id}
                      title={article.title}
                      description={article.description}
                      thumbnail={article.thumbnail}
                      link={{ to: `/articles/${article.slug}`, search: { tab: activeTab } }}
                      tags={article.tags || []}
                      articleType={article.articleType}
                      readTime={article.readTime}
                      updatedAt={article.updatedAt || article.publishedAt}
                    />
                  ))
              ) : (
                <div className="py-8 text-center text-slate-500 col-span-2">No guides available.</div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Videos Tab Content */}
      {activeTab === 'Videos' && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">Videos</h2>
          </div>
          <div className="mb-4">
            <input
              type="text"
              className="w-full md:w-1/2 border border-slate-200 rounded-xl bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
              placeholder="Search videos by title or description..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {isLoading ? (
            <SkeletonGrid count={4} cols="grid-cols-1 md:grid-cols-2">{() => <ArticleCardSkeleton />}</SkeletonGrid>
          ) : isError ? (
            <div className="py-8 text-center text-red-500">Error loading articles.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles && articles.filter(a => a.articleType === 'VIDEO')
                .filter(a => a.title.toLowerCase().includes(search.toLowerCase()) || a.description?.toLowerCase().includes(search.toLowerCase()))
                .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()).length > 0 ? (
                articles.filter(a => a.articleType === 'VIDEO')
                  .filter(a => a.title.toLowerCase().includes(search.toLowerCase()) || a.description?.toLowerCase().includes(search.toLowerCase()))
                  .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
                  .map(article => (
                    <ArticleCard
                      key={article.id}
                      title={article.title}
                      description={article.description}
                      thumbnail={article.thumbnail}
                      link={{ to: `/articles/${article.slug}`, search: { tab: activeTab } }}
                      tags={article.tags || []}
                      articleType={article.articleType}
                      readTime={article.readTime}
                      updatedAt={article.updatedAt || article.publishedAt}
                    />
                  ))
              ) : (
                <div className="py-8 text-center text-slate-500 col-span-3">No videos available.</div>
              )}
            </div>
          )}
        </div>
      )}
      {/* Printable Tab Content */}
      {activeTab === 'Downloads' && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl">Printable Guides</h2>
          </div>
          <div className="mb-4">
            <input
              type="text"
              className="w-full md:w-1/2 border border-slate-200 rounded-xl bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
              placeholder="Search printable guides by title or description..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {isLoading ? (
            <SkeletonGrid count={4} cols="grid-cols-1 md:grid-cols-2">{() => <ArticleCardSkeleton />}</SkeletonGrid>
          ) : isError ? (
            <div className="py-8 text-center text-red-500">Error loading articles.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {articles && articles.filter(a => a.articleType === 'DOWNLOADABLE_PDF')
                .filter(a => a.title.toLowerCase().includes(search.toLowerCase()) || a.description?.toLowerCase().includes(search.toLowerCase()))
                .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()).length > 0 ? (
                articles.filter(a => a.articleType === 'DOWNLOADABLE_PDF')
                  .filter(a => a.title.toLowerCase().includes(search.toLowerCase()) || a.description?.toLowerCase().includes(search.toLowerCase()))
                  .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
                  .map(article => (
                    <ArticleCard
                      key={article.id}
                      title={article.title}
                      description={article.description}
                      thumbnail={article.thumbnail}
                      link={{ to: `/articles/${article.slug}`, search: { tab: activeTab } }}
                      tags={article.tags || []}
                      articleType={article.articleType}
                      readTime={article.readTime}
                      updatedAt={article.publishedAt || article.updatedAt}
                    />
                  ))
              ) : (
                <div className="py-8 text-center text-slate-500 col-span-3">No printable guides available.</div>
              )}
            </div>
          )}
        </div>
      )}
      {/* By Skill Tab Content */}
      {activeTab === 'By Skill' && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-semibold text-slate-900 sm:text-2xl mb-2">Learn by Skill</h2>
          </div>
          <div className="mb-4">
            <input
              type="text"
              className="w-full md:w-1/2 border border-slate-200 rounded-xl bg-white px-4 py-3 text-base text-slate-900 placeholder-slate-400 outline-none transition-colors focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
              placeholder="Search by skill, title, or description..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {isLoading ? (
            <SkeletonGrid count={4} cols="grid-cols-1 md:grid-cols-2">{() => <ArticleCardSkeleton />}</SkeletonGrid>
          ) : isError ? (
            <div className="py-8 text-center text-red-500">Error loading articles.</div>
          ) : (
            <div className="space-y-8">
              {skillCategories
                .filter(category => category.categoryId)
                .map(category => {
                  const categoryArticles = articles?.filter(article =>
                    article.behaviours?.some(behaviour =>
                      behaviour.categoryId === category.categoryId
                    )
                  ) || [];
                  const filteredArticles = categoryArticles.filter(a =>
                    a.title.toLowerCase().includes(search.toLowerCase()) ||
                    a.description?.toLowerCase().includes(search.toLowerCase())
                  );
                  return (
                    <div key={category.id} className="">
                      <div className="mb-4 flex items-center">
                        <div className="flex-shrink-0 mr-4">
                          <div className="w-12 h-12 rounded-full bg-violet-50 flex items-center justify-center">
                            {category.icon}
                          </div>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">{category.name}</h3>
                          <p className="text-slate-600">{category.description}</p>
                        </div>
                      </div>
                      {filteredArticles.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {filteredArticles
                            .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
                            .map(article => (
                              <ArticleCard
                                key={article.id}
                                title={article.title}
                                description={article.description}
                                thumbnail={article.thumbnail}
                                link={{ to: `/articles/${article.slug}`, search: { tab: activeTab } }}
                                tags={article.tags || []}
                                articleType={article.articleType}
                                readTime={article.readTime}
                                updatedAt={article.updatedAt || article.publishedAt}
                              />
                            ))}
                        </div>
                      ) : (
                        <div className="text-center text-slate-500 py-8">
                          No content available for {category.name} yet.
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
